import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { FullPageLoader } from "../../components/ui/LoadingSpinner";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { Button } from "../../components/ui/Button";

export default function SSOCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setTokens, fetchMe, fetchOrganizations } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search || location.hash.replace(/^#.*\?/, ""));

    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const errorParam = params.get("error");

    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      return;
    }

    if (accessToken && refreshToken) {
      setTokens(accessToken, refreshToken);
      Promise.all([fetchMe(), fetchOrganizations()])
        .then(() => {
          navigate("/", { replace: true });
        })
        .catch(() => {
          setError("Failed to load user profile after SSO login.");
        });
    } else {
      setError("Missing authentication tokens from SSO provider.");
    }
  }, [location, setTokens, fetchMe, fetchOrganizations, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-900 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md rounded-2xl border border-surface-700/50 bg-surface-800/60 backdrop-blur-xl p-8 shadow-2xl text-center"
        >
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15">
            <AlertTriangle className="h-8 w-8 text-red-400" />
          </div>
          <h2 className="text-lg font-semibold text-surface-100 mb-2">
            SSO Login Failed
          </h2>
          <p className="text-sm text-surface-400 mb-6">{error}</p>
          <Button
            variant="secondary"
            onClick={() => navigate("/login", { replace: true })}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </Button>
        </motion.div>
      </div>
    );
  }

  return <FullPageLoader label="Completing SSO login..." />;
}
